import React, { useState, useEffect } from 'react';
import {
  Home, Settings, Package, FileText, BarChart3, Calculator,
  Users, Gem, DollarSign, Eye, ChevronDown, Building2, Shield, Upload, Scan,
  TrendingUp, Gift, RotateCcw, ShoppingBag, LogOut, Receipt, Wrench, Plus, QrCode, Image, BookOpen
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import brand from '../../config/brand';
import UserProfileModal from './UserProfileModal';
import { APP_VERSION } from '../../version';

const Sidebar = ({ currentModule, handleMenuClick }) => {
  const { user, logout } = useAuth();
  const { canAccessPage } = usePermissions();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const expanded = hovered;

  // Permission-based access control
  const hasAccess = (pageId) => {
    if (!user) return false;
    return canAccessPage(pageId);
  };

  const menuItems = [
    {
      id: 'admin',
      title: 'Admin',
      submenu: [
        { id: 'user-management', title: 'User Management', icon: Users },
        { id: 'permissions-management', title: 'User Permissions', icon: Shield },
        { id: 'material-management', title: 'Gemstone & Materials', icon: Gem },
        { id: 'category-management', title: 'Product Categories', icon: Package },
        { id: 'vendor-management', title: 'Vendor Details', icon: Building2 },
        { id: 'upload-profiles', title: 'Upload Profiles', icon: Settings },
        { id: 'billing-settings', title: 'Billing Settings', icon: Settings },
        { id: 'vendor-import', title: 'Vendor Import', icon: Upload },
        { id: 'cost-breakdown', title: 'Cost Breakdown', icon: Calculator }
      ].filter(item => hasAccess(item.id))
    },
    {
      id: 'accounting',
      title: 'Accounting',
      submenu: [
        { id: 'vendor-ledger', title: 'Vendor Ledger', icon: BookOpen },
        { id: 'accounting-summary', title: 'Accounting Summary', icon: BarChart3 }
      ].filter(item => hasAccess(item.id))
    },
    {
      id: 'inventory',
      title: 'Inventory',
      submenu: [
        { id: 'view-inventory', title: 'View Inventory', icon: Eye },
        { id: 'add-inventory', title: 'Add Inventory', icon: Plus },
        { id: 'inventory-pictures', title: 'Inventory Pictures', icon: Image }
      ].filter(item => hasAccess(item.id))
    },
    {
      id: 'billing',
      title: 'Billing',
      submenu: [
        { id: 'indian-billing', title: 'INR Billing', icon: DollarSign },
        { id: 'us-billing', title: 'USD Billing', icon: DollarSign },
        { id: 'estimate-cart', title: 'Multi-Item Billing', icon: Calculator },
        { id: 'billing-history', title: 'Billing History', icon: Receipt },
        { id: 'jewelry-returns', title: 'Customer Returns', icon: RotateCcw }
      ].filter(item => hasAccess(item.id))
    },
    {
      id: 'estimates',
      title: 'Estimates',
      submenu: [
        { id: 'inr-estimate', title: 'INR Estimate', icon: Calculator },
        { id: 'usd-estimate', title: 'USD Estimate', icon: Calculator },
        { id: 'view-estimates', title: 'View Estimates', icon: Eye }
      ].filter(item => hasAccess(item.id))
    },
    {
      id: 'report',
      title: 'Report',
      submenu: [
        { id: 'available-stock', title: 'Available Stock', icon: Package },
        { id: 'sold-items', title: 'Sold Items', icon: ShoppingBag },
        { id: 'item-aging', title: 'Item Aging', icon: TrendingUp },
        { id: 'vendor-stock', title: 'Vendor Stock', icon: Users },
        { id: 'sales-report', title: 'Sales by Vendor', icon: BarChart3 },
        { id: 'customer-list', title: 'Customer List', icon: Users }
      ].filter(item => hasAccess(item.id))
    },
    {
      id: 'utilities',
      title: 'Utilities',
      submenu: [
        { id: 'manual-rate-entry', title: 'Rate Manager', icon: Calculator },
        { id: 'inventory-upload', title: 'Import from Excel', icon: Upload },
        { id: 'exhibition-reconciliation', title: 'Reconciliation', icon: Scan },
        { id: 'barcode-generator', title: 'Barcode Generator', icon: Scan },
        { id: 'missing-images', title: 'Missing Photos', icon: Eye },
        { id: 'catalog-settings', title: 'Customer Catalog', icon: QrCode }
      ].filter(item => hasAccess(item.id))
    },
    {
      id: 'schemes',
      title: 'Gold Schemes',
      submenu: [
        { id: 'scheme-management', title: 'Manage Schemes', icon: Settings },
        { id: 'scheme-enroll', title: 'Enroll Customer', icon: Users },
        { id: 'scheme-payment', title: 'Record Payments', icon: DollarSign },
        { id: 'scheme-dashboard', title: 'My Schemes', icon: TrendingUp },
        { id: 'scheme-redemption', title: 'Redemptions', icon: Gift },
        { id: 'scheme-reports', title: 'Reports', icon: BarChart3 }
      ].filter(item => hasAccess(item.id))
    }
  ].filter(section => section.submenu.length > 0); // Remove empty sections

  // Keep the active section open when currentModule changes
  useEffect(() => {
    const activeSection = menuItems.find(item =>
      item.submenu.some(sub => sub.id === currentModule)
    );
    if (activeSection) setActiveDropdown(activeSection.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModule]);

  // Close dropdowns when sidebar collapses
  useEffect(() => {
    if (!expanded) setActiveDropdown(null);
  }, [expanded]);

  const toggleDropdown = (dropdownId) => {
    setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
  };

  return (
    <div
      className={`bg-gray-800 text-white fixed left-0 top-0 z-40 flex flex-col transition-all duration-200 ease-in-out ${expanded ? 'w-64 shadow-2xl' : 'w-16'}`} style={{ height: 'calc(100vh - 28px)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Logo area */}
      <div className={`flex-shrink-0 pt-3 pb-2 overflow-hidden ${expanded ? 'px-4' : 'px-2'}`}>
        {expanded ? (
          <div className="flex flex-col items-center mb-2">
            {brand.logo ? (
              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-600 bg-[#F7F1ED] p-1.5">
                <img src={brand.logo} alt={brand.name} className="h-14 w-auto object-contain block" />
              </div>
            ) : (
              <div className="rounded-xl shadow-lg border border-gray-600 bg-amber-500 p-3 flex items-center justify-center w-16 h-16">
                <span className="text-white text-2xl">💎</span>
              </div>
            )}
            <p className="text-white text-sm font-semibold mt-2 tracking-wide">{brand.name}</p>
            {brand.tagline && <p className="text-gray-400 text-xs tracking-widest uppercase">{brand.tagline}</p>}
          </div>
        ) : (
          <div className="flex justify-center mb-2">
            {brand.logo ? (
              <div className="rounded-lg overflow-hidden border border-gray-600 bg-[#F7F1ED] p-1">
                <img src={brand.logo} alt={brand.name} className="h-8 w-8 object-contain block" />
              </div>
            ) : (
              <div className="rounded-lg border border-gray-600 bg-amber-500 p-1.5 flex items-center justify-center w-10 h-10">
                <span className="text-white text-lg">💎</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto pb-4 px-2">
        {/* Dashboard */}
        <div className="mb-1">
          <button
            onClick={() => handleMenuClick('dashboard')}
            title={!expanded ? 'Dashboard' : ''}
            className={`w-full flex items-center p-2.5 rounded-lg transition-colors ${currentModule === 'dashboard' ? 'bg-blue-600' : 'hover:bg-gray-700'} ${expanded ? 'space-x-3' : 'justify-center'}`}
          >
            <Home size={20} className="flex-shrink-0" />
            {expanded && <span className="whitespace-nowrap">Dashboard</span>}
          </button>
        </div>

        {menuItems.map((item) => {
          const SectionIcon = {
            admin: Settings, inventory: Package, billing: FileText,
            estimates: Calculator, report: BarChart3, utilities: Wrench,
            schemes: TrendingUp, accounting: BookOpen
          }[item.id] || Settings;

          return (
            <div key={item.id} className="mb-1">
              <button
                onClick={() => expanded && toggleDropdown(item.id)}
                title={!expanded ? item.title : ''}
                className={`w-full flex items-center p-2.5 rounded-lg hover:bg-gray-700 transition-colors ${expanded ? 'justify-between' : 'justify-center'}`}
              >
                <div className={`flex items-center ${expanded ? 'space-x-3' : ''}`}>
                  <SectionIcon size={20} className="flex-shrink-0" />
                  {expanded && <span className="whitespace-nowrap">{item.title}</span>}
                </div>
                {expanded && (
                  <ChevronDown
                    size={16}
                    className={`transition-transform flex-shrink-0 ${activeDropdown === item.id ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {expanded && activeDropdown === item.id && (
                <div className="ml-4 mt-1 space-y-1">
                  {item.submenu.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => handleMenuClick(subItem.id)}
                      className={`w-full flex items-center space-x-3 p-2 rounded-lg text-sm transition-colors ${currentModule === subItem.id ? 'bg-blue-600' : 'hover:bg-gray-700'}`}
                    >
                      <subItem.icon size={16} className="flex-shrink-0" />
                      <span className="whitespace-nowrap">{subItem.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User Info + Logout */}
      <div className={`flex-shrink-0 border-t border-gray-700 p-3 overflow-hidden ${!expanded ? 'flex flex-col items-center gap-2' : ''}`}>
        {expanded ? (
          <>
            <button
              onClick={() => setShowProfileModal(true)}
              className="flex items-center gap-3 mb-2 w-full rounded-lg hover:bg-gray-700 p-1 transition-colors text-left"
              title="Edit Profile"
            >
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {user?.username?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-white truncate">{user?.username || 'User'}</div>
                <div className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ') || 'guest'}</div>
              </div>
            </button>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:bg-red-600 hover:text-white transition-colors"
            >
              <LogOut size={16} />
              <span className="whitespace-nowrap">Logout</span>
            </button>
            <div className="text-center text-[10px] text-gray-500 mt-2">v{APP_VERSION}</div>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowProfileModal(true)}
              title={`${user?.username || 'User'} — Edit Profile`}
              className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm hover:bg-blue-500 transition-colors"
            >
              {user?.username?.charAt(0)?.toUpperCase() || '?'}
            </button>
            <button onClick={logout} title="Logout" className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:bg-red-600 hover:text-white transition-colors">
              <LogOut size={16} />
            </button>
          </>
        )}
      </div>

      {showProfileModal && <UserProfileModal onClose={() => setShowProfileModal(false)} />}
    </div>
  );
};

export default Sidebar;